/**
 * Learning Moments → Normalisation Runs
 *
 * A viewer for the offline insight-category generation runs. Deliberately does
 * NO processing: rows are shown exactly as the offline pipeline published them.
 * Everything that shapes this data — extraction, normalisation, grouping —
 * happens in docs/InsightCategoryGeneration/scripts and is reviewed here.
 *
 * The point of the page is the raw → normalised pair: it is the only way to see
 * what normalisation preserved and what it destroyed.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { SearchFilterBar, type FilterDef } from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { LearningMomentTabs } from './LearningMomentTabs';
import './InsightRuns.css';

interface RunSummary {
  runId: string;
  source: string;
  cascade: string;
  env: string;
  model?: string;
  itemCount: number;
  normalisedCount: number;
  publishedAt: string;
}

interface RunItem {
  itemId: string;
  submissionId?: string | null;
  questionId?: string | null;
  subject?: string | null;
  topic?: string | null;
  stepNumber?: number | null;
  rawText: string;
  normalised?: string | null;
  cascade?: boolean;
  noError?: boolean;
  confidence?: string;
}

interface ItemsResponse {
  runId: string;
  items: RunItem[];
  subjects: string[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 50;

export function InsightRuns() {
  // One committed values map, as every other admin list page does — the bar owns
  // its own drafts until Fetch.
  const [applied, setApplied] = useState<Record<string, string>>({ run: '', subject: '', review: '', search: '' });
  const [page, setPage] = useState(1);
  const subjectLabel = useSubjectLabel();

  const { run: runId, subject, review, search } = applied;
  const flagged = review === 'flagged';

  const runs = useQuery({
    queryKey: ['insight-runs'],
    queryFn: () => fetchWithAdmin<{ runs: RunSummary[] }>('/console/insight-runs'),
  });

  // Default to the newest run once the list arrives.
  const activeRun = runId || runs.data?.runs[0]?.runId || '';

  const items = useQuery({
    queryKey: ['insight-run-items', activeRun, page, subject, flagged, search],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (subject) p.set('subject', subject);
      if (flagged) p.set('flagged', 'true');
      if (search) p.set('search', search);
      return fetchWithAdmin<ItemsResponse>(`/console/insight-runs/${activeRun}/items?${p}`);
    },
    enabled: !!activeRun,
  });

  const filterDefs: FilterDef[] = [
    {
      key: 'run',
      placeholder: 'Run',
      required: true,
      options: (runs.data?.runs ?? []).map(r => ({
        value: r.runId,
        label: `${r.runId} · ${r.source} · ${r.itemCount.toLocaleString()} items`,
      })),
    },
    {
      key: 'subject',
      placeholder: 'All subjects',
      options: (items.data?.subjects ?? []).map(sub => ({ value: sub, label: subjectLabel(sub) })),
    },
    {
      key: 'review',
      placeholder: 'All rows',
      options: [{ value: 'flagged', label: 'Needs a look' }],
    },
  ];

  const totalPages = items.data ? Math.ceil(items.data.total / items.data.pageSize) : 0;
  const summary = runs.data?.runs.find(r => r.runId === activeRun);

  return (
    <div className="lm-page">
      <div className="lm-header">
        <h1>Learning Moments</h1>
      </div>
      <LearningMomentTabs active="runs" />

      <p className="ir-intro">
        Offline normalisation runs, shown exactly as published — no processing happens on this
        page. Read the <strong>raw</strong> and <strong>normalised</strong> columns together:
        incidental numbers should be gone, and named concepts should have survived.
      </p>

      {runs.isLoading && <p>Loading runs…</p>}
      {runs.isError && <p className="ir-error">Failed to load runs: {(runs.error as Error).message}</p>}
      {runs.data && runs.data.runs.length === 0 && (
        <p className="lm-empty">
          No runs published yet. Publish one with{' '}
          <code>docs/InsightCategoryGeneration/scripts/publish.cjs --apply</code>.
        </p>
      )}

      {runs.data && runs.data.runs.length > 0 && (
        <>
          <SearchFilterBar
            filters={filterDefs}
            values={applied}
            onChange={next => { setApplied(next); setPage(1); }}
            searchPlaceholder="Search raw or normalised text…"
          />

          {summary && (
            <div className="ir-stats">
              <span><strong>{summary.itemCount.toLocaleString()}</strong> items</span>
              <span><strong>{summary.normalisedCount.toLocaleString()}</strong> normalised</span>
              <span>source <strong>{summary.source}</strong></span>
              <span>cascade <strong>{summary.cascade}</strong></span>
              {summary.model && <span>model <strong>{summary.model}</strong></span>}
              <span>from <strong>{summary.env}</strong></span>
            </div>
          )}

          {items.isLoading && <p>Loading…</p>}
          {items.isError && <p className="ir-error">Failed: {(items.error as Error).message}</p>}

          {items.data && (
            <>
              <div className="ir-count">
                {items.data.total.toLocaleString()} rows
                {(subject || flagged || search) && ' matching filters'}
              </div>

              <table className="dt ir-table">
                <thead>
                  <tr>
                    <th>Subject / topic</th>
                    <th>Raw feedback</th>
                    <th>Normalised</th>
                    <th>Flags</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.data.items.map(it => (
                    <tr key={it.itemId}>
                      <td className="ir-where">
                        {it.subject ? subjectLabel(it.subject) : '—'}
                        {it.topic && <div className="ir-topic">{it.topic}</div>}
                      </td>
                      <td className="ir-raw">{it.rawText}</td>
                      <td className="ir-norm">
                        {it.normalised ?? <span className="ir-missing">— not normalised —</span>}
                      </td>
                      <td className="ir-flags">
                        {it.cascade && <span className="ir-flag" title="Follows from an earlier step">cascade</span>}
                        {it.noError && <span className="ir-flag" title="Does not describe a mistake">no-error</span>}
                        {it.confidence && it.confidence !== 'high' && (
                          <span className="ir-flag ir-flag-warn">{it.confidence}</span>
                        )}
                      </td>
                      <td>
                        {it.submissionId && (
                          <Link to={`/submissions/${it.submissionId}`} className="ir-link">
                            attempt →
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="ir-pager">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                  <span>Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
